/* @flow */
import * as F from 'lodash/fp';
import * as React from 'react';
import styled from 'styled-components';

import { Box, Flex, Footer, Space, Text } from '@sonyged/proc-ui';
import css from '@styled-system/css';

import { ArrowIcon, LossColor } from '../../common/components/ArrowIcon';
import type { Customer, SimulationState, Sushi } from '../rules/types';
import { CustomerIcon } from './CustomerIcon';
import { SushiContext } from '../SushiContext';
import type { SushiMapper } from '../type';
import SushiMenu from './SushiMenu';

function count(mapper: SushiMapper, eatens: Sushi[]) {
  return F.pipe(
    F.countBy(mapper.getSushiName),
    F.toPairs,
    F.map(([name, count]) => `${name}×${count}`),
    F.join('、')
  )(eatens);
}

type ProfitPanelProps = {
  sale: number,
  expense: number,
  profit: number,
};
const ProfitPanel = (props: ProfitPanelProps) => {
  let { sale, expense, profit } = props;
  return (
    <Panel>
      <PanelHeading>お金</PanelHeading>
      <Box px={[12, 12, 48]}>
        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>売り上げ</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{sale}</Text> 円
          </Box>
        </Row>

        <Row mb={[12, 12, 18]} mt={[16, 16, 28]}>
          <Box bg="gray10" pl={[12, 12, 18]}>
            <Text lineHeight={['32px', '32px', '48px']}>材料のねだん</Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize={[20, 20, 32]}>{expense}</Text> 円
          </Box>
        </Row>

        <Line />
        <Row>
          <Box py={[16, 16, 28]}>
            <Text fontSize={[16, 16, 22]} color="secondary100">
              {profit < 0 ? 'そんしたお金' : 'もうけたお金'}
            </Text>
          </Box>
          <Box textAlign="right">
            <Text color={profit < 0 ? LossColor : null} fontSize={[30, 30, 40]}>
              {Math.abs(profit)}
            </Text>
            <Text color={profit < 0 ? LossColor : null} fontSize={[16, 16, 22]}>
              {' 円 '}
            </Text>
            <ArrowIcon fontSize={[16, 16, 22].map(x => x * 1.4)} forNum={profit} />
          </Box>
        </Row>
      </Box>
    </Panel>
  );
};

const Row = styled(Flex)(
  css({
    fontSize: [14, 14, 20],
    color: 'gray100',
    '>*': { flex: 1 },
  })
);

type SoldSushiPanelProps = {
  sushiEatenByCustomers: Array<[Customer, Array<Sushi>]>,
  mapper: SushiMapper,
};
const SoldSushiPanel = (props: SoldSushiPanelProps) => {
  let { sushiEatenByCustomers, mapper } = props;
  let totalCount = F.sumBy(([, sushi]) => sushi.length)(sushiEatenByCustomers);

  return (
    <Panel>
      <PanelHeading>売れたおすし {totalCount}皿</PanelHeading>
      <Box px={[12, 12, 42]} py={[12, 12, 16]}>
        {sushiEatenByCustomers.map(([custommer, eatens], i) => (
          <FoodContainer key={i}>
            <Avatar customer={custommer} />
            {eatens.length ? (
              <Flex flexDirection="column">
                <Flex>
                  {eatens.map((sushi, sushiIndex) => (
                    <FoodIcon key={sushiIndex} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
                  ))}
                </Flex>
                <FoodName>{count(mapper, eatens)}</FoodName>
              </Flex>
            ) : (
              <Box alignSelf="center">
                <FoodName>なし</FoodName>
              </Box>
            )}
          </FoodContainer>
        ))}
      </Box>
    </Panel>
  );
};

type RemainSushiPanelProps = {
  showBothHalf?: boolean,
  firstHalf: Sushi[],
  secondHalf?: Sushi[],
  mapper: SushiMapper,
};
const RemainSushiPanel = (props: RemainSushiPanelProps) => {
  let { showBothHalf, firstHalf, secondHalf = [], mapper } = props;
  let total = firstHalf.length + secondHalf.length;
  return (
    <Panel>
      <PanelHeading>残ったおすし {total}皿</PanelHeading>
      <Box px={[18, 18, 42]} py={[12, 12, 16]}>
        <FoodContainer>
          {showBothHalf && (
            <Tag>
              <TagName>前半</TagName>
            </Tag>
          )}
          <Box flex={1} minWidth={0}>
            <FoodIconList>
              {firstHalf.map((sushi, i) => (
                <FoodIcon key={i} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
              ))}
            </FoodIconList>
            <Box>
              <FoodName>{count(mapper, firstHalf)}</FoodName>
            </Box>
          </Box>
        </FoodContainer>
        {showBothHalf && (
          <FoodContainer>
            <Tag>
              <TagName>後半</TagName>
            </Tag>
            <Box flex={1} minWidth={0}>
              <FoodIconList>
                {secondHalf.map((sushi, i) => (
                  <FoodIcon key={i} src={mapper.getSushiBwIcon(sushi)} alt="Sushi" />
                ))}
              </FoodIconList>
              <Box>
                <FoodName>{count(mapper, secondHalf)}</FoodName>
              </Box>
            </Box>
          </FoodContainer>
        )}
      </Box>
    </Panel>
  );
};

type Props = {
  customers: Array<Customer>,
  lastState: SimulationState,
  firstHalfState: ?SimulationState,
  onNext: Function,
  fromOverview?: boolean,
  half: 'first' | 'second',
  roundIndex: number,
};

export function SushiAdvanceResultScreen(props: Props) {
  const {
    customers,
    lastState: { eatens, belt },
    firstHalfState,
    onNext,
    fromOverview,
  } = props;

  const mapper = React.useContext(SushiContext);

  const allEatens = F.flatten(eatens);

  const remainingSushiFirstHalf: ?Array<Sushi> = firstHalfState
    ? firstHalfState.belt.filter(slot => slot.type === 'filled').map((x: any) => x.sushi)
    : null;
  const currentRemainingSushi: Array<Sushi> = belt.filter(slot => slot.type === 'filled').map((x: any) => x.sushi);

  let showBothHalf = false;
  let first: Array<Sushi>;
  let second: Array<Sushi>;
  if (remainingSushiFirstHalf) {
    showBothHalf = true;
    first = remainingSushiFirstHalf;
    second = currentRemainingSushi;
  } else {
    showBothHalf = false;
    first = currentRemainingSushi;
    second = [];
  }

  let menuText;
  if (props.half === 'first') {
    menuText = '組目 前半 結果';
  }

  if (props.half === 'second') {
    menuText = '組目 結果';
  }

  const remainSushiPrice = F.pipe(F.map(F.pipe(F.map(mapper.getSushiOriginalPrice), F.sum)), F.sum)([first, second]);

  let eatenPrice = F.pipe(F.map(mapper.getSushiOriginalPrice), F.sum)(allEatens);

  const received = F.pipe(F.map(mapper.getSushiPrice), F.sum)(allEatens);

  const score = received - eatenPrice - remainSushiPrice;

  let sushiEatenByCustomers = React.useMemo(() => F.zip(customers, eatens), [customers, eatens]);

  return (
    <Flex height="100%" flexDirection="column" bg="tertiary100">
      <SushiMenu
        exitLink={mapper.getExitLink()}
        query={'?skip_intro=true'}
        title={''}
        subTitle={mapper.getTitle()}
        menuText={props.roundIndex + 1 + menuText}
      />
      <Body>
        <Box flex={1} minHeight={0} overflow="auto" px={16}>
          <Space my={['12px']}>
            <Box>
              <ProfitPanel expense={remainSushiPrice + eatenPrice} profit={score} sale={received} />
              <SoldSushiPanel mapper={mapper} sushiEatenByCustomers={sushiEatenByCustomers} />
              <RemainSushiPanel mapper={mapper} showBothHalf={showBothHalf} firstHalf={first} secondHalf={second} />
            </Box>
          </Space>
        </Box>
      </Body>

      <Box bg="white">
        <Footer variant="raised">
          <Footer.MainButton onClick={onNext} variant={fromOverview ? 'outline' : 'attention'}>
            {fromOverview ? 'もどる' : '次へ'}
          </Footer.MainButton>
        </Footer>
      </Box>
    </Flex>
  );
}

const Panel = styled(Box)(
  css({
    borderStyle: 'solid',
    borderWidth: ['1px', '1px', '2px'],
    borderRadius: [12, 12, 15],
    borderColor: 'secondary20',
    maxWidth: ['440px', '440px', '540px'],
    width: '100%',
    bg: 'white',
    mx: 'auto',
    mb: '10px',
    overflow: 'hidden',
  })
);

const PanelHeading = styled(Box)(
  css({
    px: ['18px', '18px', '24px'],
    py: ['6px', '6px', '8px'],
    bg: 'secondary20',
    color: 'gray100',
    fontSize: ['mobile.small', 16, 'largePC.small'],
    lineHeight: 'title',
  })
);

const Line = styled(Box)(
  css({
    height: '2px',
    bg: 'secondary20',
  })
);

const Avatar = styled(CustomerIcon)(
  css({
    size: [36, 36, 42],
    borderRadius: '50%',
    marginRight: '8px',
  })
);

const FoodContainer = styled(Flex)(
  css({
    '&:not(:last-child)': {
      marginBottom: ['6px', '6px', '8px'],
    },
  })
);

const FoodIconList = styled.div(
  css({
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: 'gray100',
    '> *': {
      verticalAlign: 'middle',
    },
  })
);

const FoodIcon = styled('img')(
  css({
    display: 'inline-block',
    size: [40, 40, 48],
  })
);

const FoodName = styled(Text)(
  css({
    fontSize: '13px',
    color: 'secondary100',
    lineHeight: 1,
  })
);

const Tag = styled(Flex)(
  css({
    flexShrink: 0,
    mt: '4px',
    height: '26px',
    width: '44px',
    bg: 'gray10',
    justifyContent: 'center',
    alignItems: 'center',
  })
);

const TagName = styled(Text)(
  css({
    fontSize: '14px',
    color: 'gray100',
  })
);

const Body = styled(Flex)(
  css({
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    pt: ['20px', '20px', '36px'],
    bg: 'tertiary100',
  })
);
